import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  inject,
} from '@angular/core';
import { ModusBreadcrumbItem } from '../utils/modus-breadcrumb.utils';

const TOOLTIP_CLASS = 'app-breadcrumb-tooltip';
const TRIGGER_SELECTOR = ':scope > a, :scope > button, :scope > span';

/** Wraps truncated breadcrumb labels in Modus tooltips that show the full name. */
@Directive({
  selector: 'modus-wc-breadcrumbs[appBreadcrumbTooltips]',
  standalone: true,
})
export class BreadcrumbTooltipsDirective implements AfterViewInit, OnChanges, OnDestroy {
  private readonly el = inject(ElementRef<HTMLElement>);

  @Input() breadcrumbTooltipItems: readonly Pick<ModusBreadcrumbItem, 'tooltip'>[] = [];

  private mutationObserver?: MutationObserver;
  private rafId = 0;

  ngAfterViewInit(): void {
    this.scheduleSync();

    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(() => this.scheduleSync());
      this.mutationObserver.observe(this.getBreadcrumbRoot(), { childList: true, subtree: true });
    }
  }

  ngOnChanges(): void {
    this.scheduleSync();
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  }

  private getBreadcrumbRoot(): ParentNode {
    const host = this.el.nativeElement;
    return host.shadowRoot ?? host;
  }

  private scheduleSync(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    this.rafId = requestAnimationFrame(() => {
      this.rafId = requestAnimationFrame(() => {
        this.rafId = 0;
        this.syncTooltips();
      });
    });
  }

  private syncTooltips(): void {
    const listItems = this.getBreadcrumbRoot().querySelectorAll(
      'nav.modus-wc-breadcrumbs > ol > li'
    );
    listItems.forEach((li: Element, index: number) => {
      const tooltipText = this.breadcrumbTooltipItems[index]?.tooltip?.trim();
      const existingTooltip = li.querySelector(`:scope > modus-wc-tooltip.${TOOLTIP_CLASS}`);

      if (!tooltipText) {
        this.unwrapTooltip(li, existingTooltip);
        return;
      }

      if (existingTooltip instanceof HTMLElement) {
        existingTooltip.setAttribute('content', tooltipText);
        this.applyTooltipInlineStyles(existingTooltip);
        return;
      }

      const trigger = li.querySelector(TRIGGER_SELECTOR);
      if (!(trigger instanceof HTMLElement)) {
        return;
      }

      const tooltip = this.createTooltip(tooltipText);
      li.insertBefore(tooltip, trigger);
      tooltip.appendChild(trigger);
    });
  }

  private unwrapTooltip(li: Element, tooltip: Element | null): void {
    if (!(tooltip instanceof HTMLElement)) {
      return;
    }

    const trigger = tooltip.querySelector('a, button, span');
    if (trigger instanceof HTMLElement) {
      li.insertBefore(trigger, tooltip);
    }
    tooltip.remove();
  }

  private createTooltip(content: string): HTMLElement {
    const tooltip = document.createElement('modus-wc-tooltip');
    tooltip.className = TOOLTIP_CLASS;
    tooltip.setAttribute('content', content);
    tooltip.setAttribute('position', 'bottom');
    Object.assign(tooltip.style, {
      display: 'inline-flex',
      width: 'auto',
      flexShrink: '0',
    });
    this.applyTooltipInlineStyles(tooltip);
    return tooltip;
  }

  private applyTooltipInlineStyles(tooltip: HTMLElement): void {
    const inject = (): void => {
      const root = tooltip.shadowRoot;
      if (!root || root.querySelector('[data-breadcrumb-tooltip]')) {
        return;
      }

      const style = document.createElement('style');
      style.setAttribute('data-breadcrumb-tooltip', '');
      style.textContent = `
        :host {
          display: inline-flex !important;
          width: auto !important;
          min-width: 0 !important;
          max-width: none !important;
          overflow: visible !important;
          box-sizing: border-box !important;
          vertical-align: middle;
        }
        div {
          display: inline-flex !important;
          width: auto !important;
          min-width: 0 !important;
          max-width: none !important;
          overflow: visible !important;
          box-sizing: border-box !important;
        }
      `;
      root.appendChild(style);
    };

    if (tooltip.shadowRoot) {
      inject();
      return;
    }

    requestAnimationFrame(() => requestAnimationFrame(inject));
  }
}
